// src/pages/ReportOrePage.js
import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { BarChart2, Users, FolderOpen, Download, AlertTriangle } from "lucide-react";
import * as XLSX from "xlsx";
import { numIt, STATI_ODA_CONTEGGIATI, imponibileOda } from "../utils/economiaCommessa";
import { segmentiGiornata } from "../utils/segmentiGiornata";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
               "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const MESI_SHORT = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

function fmtOre(min) {
  if (!min || min === 0) return "—";
  return (min / 60).toFixed(1) + "h";
}
function pct(val, tot) {
  if (!tot || tot === 0) return "0%";
  return Math.round((val / tot) * 100) + "%";
}
function pctNum(val, tot) {
  if (!tot || tot === 0) return 0;
  return Math.round((val / tot) * 100);
}

async function caricaPresenze(orgId, anno, mese) {
  const mesi = mese !== null ? [mese] : Array.from({length:12},(_,i)=>i+1);
  const allDocs = [];
  await Promise.all(mesi.map(async m => {
    const snap = await getDocs(query(collection(db,"presenze"),
      where("orgId","==",orgId), where("anno","==",anno), where("mese","==",m)));
    snap.docs.forEach(d => allDocs.push(d.data()));
  }));
  return allDocs;
}


// Stesso calcolo del cartellino (utils/segmentiGiornata.js)
function calcolaSegmenti(timbrature) {
  if (!timbrature || timbrature.length === 0) return [];
  return segmentiGiornata(timbrature).segmenti;
}

// ── Grafico a barre semplice ─────────────────────────────────────────────────
function GraficoMensile({ datiMensili, operaioUid }) {
  if (!datiMensili || datiMensili.length === 0) return null;
  const maxMin = Math.max(...datiMensili.map(d => d.minuti), 1);
  const oreStd = 8 * 22; // ~176h mese standard

  return (
    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", padding:"16px 20px", marginBottom:20 }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>
        Andamento ore mensili {new Date().getFullYear()}
      </div>
      <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:80 }}>
        {datiMensili.map((d, i) => {
          const h = Math.round((d.minuti / maxMin) * 72);
          const isCurrentMonth = i === new Date().getMonth();
          const underStd = d.minuti > 0 && d.minuti < oreStd * 60 * 0.7;
          const col = d.minuti === 0 ? "#e5e7eb" : underStd ? "#fbbf24" : "#7c3aed";
          return (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
              <div style={{ fontSize:8, color: d.minuti>0?"#374151":"#d1d5db", fontWeight:600 }}>
                {d.minuti > 0 ? (d.minuti/60).toFixed(0)+"h" : ""}
              </div>
              <div style={{ width:"100%", height:h||2, borderRadius:3, background:col,
                outline: isCurrentMonth ? "2px solid #7c3aed" : "none",
                outlineOffset:1, minHeight:2 }}/>
              <div style={{ fontSize:8, color: isCurrentMonth?"#7c3aed":"#9ca3af", fontWeight: isCurrentMonth?700:400 }}>
                {MESI_SHORT[i]}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:12, marginTop:8 }}>
        <span style={{ fontSize:9, color:"#9ca3af", display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ width:8, height:8, background:"#7c3aed", borderRadius:2, display:"inline-block" }}/> Ore regolari
        </span>
        <span style={{ fontSize:9, color:"#9ca3af", display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ width:8, height:8, background:"#fbbf24", borderRadius:2, display:"inline-block" }}/> Sotto soglia (70% mese standard)
        </span>
      </div>
    </div>
  );
}

// ── Export CSV ────────────────────────────────────────────────────────────────
function esportaCSV(dati, nomeFile) {
  const righe = [Object.keys(dati[0]).join(";")];
  dati.forEach(r => righe.push(Object.values(r).join(";")));
  const blob = new Blob(["\uFEFF" + righe.join("\n")], { type:"text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nomeFile + ".csv"; a.click();
  URL.revokeObjectURL(url);
}

// ── Rilevamento anomalie ──────────────────────────────────────────────────────
function rilevaAnomalieOperaio(datiOperaio, dipendente, periodoTipo) {
  const anomalie = [];
  if (!datiOperaio) return anomalie;
  const sogliaStrMin = periodoTipo === "mese" ? 10*60 : 120*60; // 10h mese, 120h anno
  const sogliaLavMin = periodoTipo === "mese" ? 20*60 : 200*60; // <20h mese, <200h anno = inattivo
  if (datiOperaio.totStr > sogliaStrMin)
    anomalie.push({ tipo:"warning", msg:`Straordinari elevati: ${fmtOre(datiOperaio.totStr)}` });
  if (datiOperaio.totLav > 0 && datiOperaio.totLav < sogliaLavMin)
    anomalie.push({ tipo:"info", msg:`Ore basse nel periodo: ${fmtOre(datiOperaio.totLav)}` });
  if (datiOperaio.totLav === 0)
    anomalie.push({ tipo:"danger", msg:"Nessuna ora registrata nel periodo" });
  return anomalie;
}

export default function ReportOrePage({ onNavigate }) {
  const { effectiveOrgId: orgId } = useOrg();

  const oggi = new Date();
  const [tab, setTab] = useState("operaio");
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth() + 1);
  const [periodoTipo, setPeriodoTipo] = useState("mese");
  const [dipendenti, setDipendenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [operaioSel, setOperaioSel] = useState(null);
  const [datiPresenze, setDatiPresenze] = useState([]);
  const [datiAnno, setDatiAnno] = useState([]); // tutti i mesi anno per grafico
  const [loading, setLoading] = useState(false);
  const [movimentiContab, setMovimentiContab] = useState([]);
  const [nfContab, setNfContab] = useState([]);
  const [loadingContab, setLoadingContab] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"dipendenti"), where("organizationId","==",orgId)),
      snap => setDipendenti(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"projects"), where("organizationId","==",orgId), where("isParent","==",false)),
      snap => setCommesse(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const meseQ = periodoTipo === "mese" ? mese : null;
    caricaPresenze(orgId, anno, meseQ)
      .then(docs => { setDatiPresenze(docs); setLoading(false); })
      .catch(() => setLoading(false));
  }, [orgId, anno, mese, periodoTipo]);

  // Carica movimenti economici per report contabilità
  useEffect(() => {
    if (!orgId || tab !== "cumulativo") return;
    setLoadingContab(true);
    // Carica tutti i movimenti del mese/anno selezionato
    Promise.all([
      getDocs(query(collection(db,"movimentiEconomici"), where("organizationId","==",orgId))),
      // Materiali dagli ordini (ODA) — non sono registrati come movimentiEconomici: si sommano
      // come fa il Controllo Economico (stessi stati contati, imponibile IVA esclusa). Prima si
      // leggeva un importo scritto a mano sulla NF, che poteva non coincidere con l'ordine (W435).
      getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",orgId))),
    ])
      .then(([snap, nfSnap]) => {
        setMovimentiContab(snap.docs.map(d => ({ id:d.id, ...d.data() })));
        setNfContab(nfSnap.docs.map(d => ({ id:d.id, ...d.data() })).filter(o=>!o.eliminato && STATI_ODA_CONTEGGIATI.includes(o.stato)));
        setLoadingContab(false);
      })
      .catch(() => setLoadingContab(false));
  }, [orgId, tab, anno, mese]);

  // Carica dati annuali per grafico (sempre tutti i 12 mesi dell'anno)
  useEffect(() => {
    if (!orgId || !operaioSel) return;
    caricaPresenze(orgId, anno, null).then(setDatiAnno);
  }, [orgId, anno, operaioSel]);

  // Grafico mensile operaio selezionato
  const graficoMensile = useMemo(() => {
    if (!operaioSel) return [];
    return Array.from({length:12}, (_, i) => {
      const meseNum = i + 1;
      const docsMese = datiAnno.filter(d => d.uid === operaioSel.nexoraUid && d.mese === meseNum);
      let minuti = 0;
      docsMese.forEach(doc => {
        Object.entries(doc.giorni||{}).forEach(([gs, g]) => {
          const giorno = parseInt(gs);
          const data = new Date(anno, i, giorno);
          const isSabDom = data.getDay()===0||data.getDay()===6;
          const segs = calcolaSegmenti(g.timbrature||[]);
          const minLav = segs.filter(s=>!s.speciale).reduce((s,seg)=>s+seg.minuti,0);
          const PAUSA = minLav >= 360 ? 60 : 0;
          minuti += Math.max(0, minLav - PAUSA);
        });
      });
      return { mese: meseNum, minuti };
    });
  }, [operaioSel, datiAnno, anno]);

  // Dati operaio selezionato
  const datiOperaio = useMemo(() => {
    if (!operaioSel) return null;
    const docsDip = datiPresenze.filter(d => d.uid === operaioSel.nexoraUid);
    let totLav = 0, totStr = 0, totFerie = 0, totPerm = 0, totMal = 0;
    const orePerCommessa = {};

    docsDip.forEach(doc => {
      Object.entries(doc.giorni||{}).forEach(([gs, g]) => {
        const giorno = parseInt(gs);
        const data = new Date(doc.anno, doc.mese-1, giorno);
        const isSabDom = data.getDay()===0||data.getDay()===6;
        const segs = calcolaSegmenti(g.timbrature||[]);
        const minLav = segs.filter(s=>!s.speciale).reduce((s,seg)=>s+seg.minuti,0);
        const PAUSA = minLav >= 360 ? 60 : 0;
        const minNetti = Math.max(0, minLav - PAUSA);
        totLav += minNetti;
        totStr += isSabDom ? minNetti : Math.max(0, minNetti - 8*60);
        segs.forEach(seg => {
          if (seg.speciale) {
            if (seg.tipo==="Ferie") totFerie += seg.minuti;
            else if (seg.tipo==="Permesso") totPerm += seg.minuti;
            else if (seg.tipo==="Malattia") totMal += seg.minuti;
          } else {
            const nome = seg.commessaNome || "Senza commessa";
            orePerCommessa[nome] = (orePerCommessa[nome]||0) + seg.minuti;
          }
        });
      });
    });
    const commesseArr = Object.entries(orePerCommessa).sort((a,b)=>b[1]-a[1]);
    return { totLav, totStr, totFerie, totPerm, totMal, commesseArr };
  }, [operaioSel, datiPresenze]);

  // Anomalie operaio
  const anomalie = useMemo(() =>
    rilevaAnomalieOperaio(datiOperaio, operaioSel, periodoTipo),
    [datiOperaio, operaioSel, periodoTipo]);

  // Dati per commessa con dettaglio operai
  const datiCommesse = useMemo(() => {
    const map = {};
    datiPresenze.forEach(doc => {
      Object.entries(doc.giorni||{}).forEach(([gs, g]) => {
        calcolaSegmenti(g.timbrature||[]).forEach(seg => {
          if (seg.speciale || !seg.commessaNome) return;
          if (!map[seg.commessaNome]) map[seg.commessaNome] = { minuti:0, operai:{} };
          map[seg.commessaNome].minuti += seg.minuti;
          const uid = doc.uid;
          map[seg.commessaNome].operai[uid] = (map[seg.commessaNome].operai[uid]||0) + seg.minuti;
        });
      });
    });
    return Object.entries(map).sort((a,b)=>b[1].minuti-a[1].minuti)
      .map(([nome, dati]) => ({ nome, ...dati }));
  }, [datiPresenze]);

  const periodoLabel = periodoTipo==="mese" ? `${MESI[mese-1]} ${anno}` : `Anno ${anno}`;
  const anni = [oggi.getFullYear(), oggi.getFullYear()-1, oggi.getFullYear()-2];

  // Export operaio


  const esportaXLSX = (righe, nomeFile, nomeSheet) => {
    // Supporta sia array di oggetti che array di array
    let ws;
    if (righe.length > 0 && Array.isArray(righe[0])) {
      ws = XLSX.utils.aoa_to_sheet(righe);
    } else {
      ws = XLSX.utils.json_to_sheet(righe);
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, nomeSheet);
    XLSX.writeFile(wb, `${nomeFile}.xlsx`);
  };

  const esportaContabilita = (periodoMese) => {
    const MESI_LABEL = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

    // Filtra movimenti per periodo
    const movFiltrati = periodoMese
      ? movimentiContab.filter(m => {
          const d = m.createdAt?.toDate?.() || new Date(m.createdAt?.seconds*1000||0);
          return d.getFullYear()===anno && d.getMonth()+1===periodoMese;
        })
      : movimentiContab.filter(m => {
          const d = m.createdAt?.toDate?.() || new Date(m.createdAt?.seconds*1000||0);
          return d.getFullYear()===anno;
        });

    // Stesso filtro periodo, sulla data dell'ordine
    const nfFiltrate = nfContab.filter(o => {
      if (!o.data) return false;
      const [y,m] = o.data.split("-");
      return periodoMese ? (Number(y)===anno && Number(m)===periodoMese) : Number(y)===anno;
    });

    // Raggruppa per commessa
    const perCommessa = {};
    movFiltrati.forEach(m => {
      const comm = commesse.find(c=>c.id===m.projectId);
      const nomeComm = comm ? `${comm.codiceCommessa||""} ${comm.name}`.trim() : m.projectId||"—";
      if (!perCommessa[nomeComm]) perCommessa[nomeComm] = { manodopera:0, materiali:0, trasporti:0, noteSpese:0, altro:0, totale:0 };
      const imp = numIt(m.importo)||0;
      const cat = m.categoriaId||"";
      if(cat==="manodopera"||cat==="personale") perCommessa[nomeComm].manodopera += imp;
      else if(cat==="materiali") perCommessa[nomeComm].materiali += imp;
      else if(cat==="trasporti") perCommessa[nomeComm].trasporti += imp;
      else if(cat==="nota_spese") perCommessa[nomeComm].noteSpese += imp;
      else perCommessa[nomeComm].altro += imp;
      perCommessa[nomeComm].totale += imp;
    });
    // Materiali dagli ODA — sommati alla stessa categoria "materiali", come nel Controllo Economico
    nfFiltrate.forEach(o => {
      const comm = commesse.find(c=>c.id===o.commessaId);
      const nomeComm = comm ? `${comm.codiceCommessa||""} ${comm.name}`.trim() : o.commessaNome||o.commessaId||"—";
      if (!perCommessa[nomeComm]) perCommessa[nomeComm] = { manodopera:0, materiali:0, trasporti:0, noteSpese:0, altro:0, totale:0 };
      const imp = imponibileOda(o);
      perCommessa[nomeComm].materiali += imp;
      perCommessa[nomeComm].totale += imp;
    });

    const label = periodoMese ? `${MESI_LABEL[periodoMese-1]} ${anno}` : `Anno ${anno}`;
    const righe = [
      [`REPORT CONTABILITÀ NEXIORA — ${label}`],
      [],
      ["Commessa","Manodopera (€)","Materiali (€)","Trasporti (€)","Note Spese (€)","Altro (€)","TOTALE (€)"],
      ...Object.entries(perCommessa).map(([nome,v])=>[
        nome,
        v.manodopera.toFixed(2),v.materiali.toFixed(2),v.trasporti.toFixed(2),v.noteSpese.toFixed(2),v.altro.toFixed(2),v.totale.toFixed(2)
      ]),
      [],
      ["TOTALE",
        Object.values(perCommessa).reduce((s,v)=>s+v.manodopera,0).toFixed(2),
        Object.values(perCommessa).reduce((s,v)=>s+v.materiali,0).toFixed(2),
        Object.values(perCommessa).reduce((s,v)=>s+v.trasporti,0).toFixed(2),
        Object.values(perCommessa).reduce((s,v)=>s+v.noteSpese,0).toFixed(2),
        Object.values(perCommessa).reduce((s,v)=>s+v.altro,0).toFixed(2),
        Object.values(perCommessa).reduce((s,v)=>s+v.totale,0).toFixed(2),
      ]
    ];
    esportaXLSX(righe, `Report_Contabilita_${label.replace(/\s/g,"_")}`, "Contabilità");
  };

  const esportaOperaio = () => {
    if (!datiOperaio || !operaioSel) return;
    const nomeOp = `${operaioSel.nome||""} ${operaioSel.cognome||""}`.trim();
    const righe = datiOperaio.commesseArr.map(([nome, min]) => ({
      "Operaio": nomeOp,
      "Periodo": periodoLabel,
      "Commessa": nome,
      "Ore lavorate": (min/60).toFixed(1),
      "% sul totale": pctNum(min, datiOperaio.totLav) + "%",
    }));
    righe.push({
      "Operaio": nomeOp,
      "Periodo": periodoLabel,
      "Commessa": "TOTALE",
      "Ore lavorate": (datiOperaio.totLav/60).toFixed(1),
      "% sul totale": "100%",
    });
    esportaXLSX(righe, `Report_Ore_${nomeOp.replace(/\s+/g,"_")}_${periodoLabel.replace(/\s+/g,"_")}`, "Ore Operaio");
  };

  // Export commesse
  const esportaCommesse = () => {
    const righe = datiCommesse.map(({ nome, minuti }) => ({
      "Commessa": nome,
      "Periodo": periodoLabel,
      "Ore totali": (minuti/60).toFixed(1),
    }));
    esportaXLSX(righe, `Report_Ore_Commesse_${periodoLabel.replace(/\s+/g,"_")}`, "Ore Commesse");
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#1e1b4b,#312e81)", padding:"16px 24px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12, flexWrap:"wrap" }}>
          <BarChart2 size={20} color="#c4b5fd"/>
          <h2 style={{ fontSize:18, fontWeight:800, color:"#fff", margin:0 }}>Report Ore</h2>
          <span style={{ fontSize:12, color:"rgba(255,255,255,0.5)" }}>Riepilogo ore da cartellino presenze</span>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ display:"flex", background:"rgba(255,255,255,0.1)", borderRadius:8, overflow:"hidden" }}>
            {["mese","anno"].map(t => (
              <button key={t} onClick={() => setPeriodoTipo(t)}
                style={{ padding:"5px 14px", border:"none",
                  background: periodoTipo===t?"rgba(124,58,237,0.6)":"transparent",
                  color: periodoTipo===t?"#fff":"rgba(255,255,255,0.6)",
                  fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                {t==="mese"?"Mese":"Anno intero"}
              </button>
            ))}
          </div>
          {periodoTipo==="mese" && (
            <select value={mese} onChange={e=>setMese(Number(e.target.value))}
              style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,0.2)",
                background:"rgba(255,255,255,0.1)", color:"#fff", fontSize:12, outline:"none", fontFamily:"'Inter',sans-serif" }}>
              {MESI.map((m,i) => <option key={i} value={i+1} style={{ background:"#1e1b4b" }}>{m}</option>)}
            </select>
          )}
          <select value={anno} onChange={e=>setAnno(Number(e.target.value))}
            style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,0.2)",
              background:"rgba(255,255,255,0.1)", color:"#fff", fontSize:12, outline:"none", fontFamily:"'Inter',sans-serif" }}>
            {anni.map(a => <option key={a} value={a} style={{ background:"#1e1b4b" }}>{a}</option>)}
          </select>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginLeft:4 }}>
            {loading ? "⏳ Caricamento..." : `Periodo: ${periodoLabel}`}
          </span>
        </div>
        <div style={{ display:"flex", gap:4, marginTop:12 }}>
          {[{id:"operaio",icon:<Users size={13}/>,label:"Per dipendente"},{id:"commessa",icon:<FolderOpen size={13}/>,label:"Per commessa"},{id:"cumulativo",icon:<BarChart2 size={13}/>,label:"Cumulativo"}].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 16px",
                border:"none", borderRadius:"8px 8px 0 0",
                background:tab===t.id?"#fff":"rgba(255,255,255,0.1)",
                color:tab===t.id?"#1e1b4b":"rgba(255,255,255,0.7)",
                fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenuto */}
      <div style={{ flex:1, overflowY:"auto", padding:24, background:"#f9fafb" }}>

        {/* ── TAB OPERAIO ──────────────────────────────────────────────── */}
        {tab==="operaio" && (
          <div>
            {/* Selettore operaio */}
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Seleziona dipendente</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {dipendenti.filter(d=>d.nexoraUid).map(d => {
                  const nome = `${d.nome||""} ${d.cognome||""}`.trim();
                  const sel = operaioSel?.id===d.id;
                  return (
                    <button key={d.id} onClick={()=>setOperaioSel(sel?null:d)}
                      style={{ padding:"6px 14px", borderRadius:20,
                        border:`1px solid ${sel?"#7c3aed":"#e5e7eb"}`,
                        background:sel?"#7c3aed":"#fff", color:sel?"#fff":"#374151",
                        fontSize:12, fontWeight:sel?700:400, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      {nome||d.nome}
                      {d.ruolo && <span style={{ fontSize:10, opacity:.7, marginLeft:4 }}>({d.ruolo})</span>}
                    </button>
                  );
                })}
                {dipendenti.filter(d=>d.nexoraUid).length===0 && (
                  <div style={{ fontSize:13, color:"#9ca3af" }}>Nessun dipendente con account Nexiora collegato.</div>
                )}
              </div>
            </div>

            {operaioSel && datiOperaio && (<>
              {/* Intestazione + Export */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:8 }}>
                <div style={{ fontSize:15, fontWeight:800, color:"#111827" }}>
                  {`${operaioSel.nome||""} ${operaioSel.cognome||""}`.trim()} — {periodoLabel}
                </div>
                <button onClick={esportaOperaio}
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px",
                    border:"1px solid #e5e7eb", borderRadius:8, background:"#fff",
                    color:"#374151", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  <Download size={13}/> Esporta Excel
                </button>
              </div>

              {/* Anomalie */}
              {anomalie.length > 0 && (
                <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:6 }}>
                  {anomalie.map((a, i) => {
                    const colors = { danger:["#fef2f2","#dc2626"], warning:["#fffbeb","#d97706"], info:["#eff6ff","#2563eb"] };
                    const [bg, col] = colors[a.tipo]||colors.info;
                    return (
                      <div key={i} style={{ background:bg, border:`1px solid ${col}33`, borderLeft:`3px solid ${col}`,
                        borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:8 }}>
                        <AlertTriangle size={14} color={col}/>
                        <span style={{ fontSize:12, color:col, fontWeight:600 }}>{a.msg}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Badge riepilogativi */}
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:20 }}>
                {[
                  { label:"Ore lavorate", val:datiOperaio.totLav, col:"#059669", bg:"#d1fae5" },
                  { label:"Straordinari",  val:datiOperaio.totStr, col:"#d97706", bg:"#fef3c7" },
                  { label:"Ferie",          val:datiOperaio.totFerie, col:"#2563eb", bg:"#dbeafe" },
                  { label:"Permessi",       val:datiOperaio.totPerm, col:"#7c3aed", bg:"#ede9fe" },
                  { label:"Malattia",       val:datiOperaio.totMal, col:"#dc2626", bg:"#fecaca" },
                ].map(({ label, val, col, bg }) => (
                  <div key={label} style={{ background:bg, borderRadius:10, padding:"10px 18px", minWidth:100 }}>
                    <div style={{ fontSize:10, color:col, fontWeight:700 }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:col }}>{fmtOre(val)}</div>
                  </div>
                ))}
              </div>

              {/* Grafico mensile */}
              {periodoTipo==="mese" && (
                <GraficoMensile datiMensili={graficoMensile} operaioUid={operaioSel.nexoraUid}/>
              )}

              {/* Tabella ore per commessa */}
              <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                <div style={{ padding:"12px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, color:"#374151" }}>
                  Ore per commessa
                </div>
                {datiOperaio.commesseArr.length===0 ? (
                  <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:13 }}>Nessuna timbratura per commessa nel periodo</div>
                ) : (
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ background:"#f9fafb" }}>
                        <th style={{ padding:"8px 16px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6b7280" }}>Commessa</th>
                        <th style={{ padding:"8px 16px", textAlign:"right", fontSize:11, fontWeight:700, color:"#6b7280" }}>Ore</th>
                        <th style={{ padding:"8px 16px", textAlign:"right", fontSize:11, fontWeight:700, color:"#6b7280" }}>%</th>
                        <th style={{ padding:"8px 16px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6b7280" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {datiOperaio.commesseArr.map(([nome, min], i) => (
                        <tr key={i} style={{ borderTop:"1px solid #f3f4f6" }}>
                          <td style={{ padding:"8px 16px", fontSize:13, color:"#111827", fontWeight:500 }}>{nome}</td>
                          <td style={{ padding:"8px 16px", textAlign:"right", fontSize:13, fontWeight:700, color:"#059669" }}>{fmtOre(min)}</td>
                          <td style={{ padding:"8px 16px", textAlign:"right", fontSize:12, color:"#6b7280" }}>{pct(min, datiOperaio.totLav)}</td>
                          <td style={{ padding:"8px 16px" }}>
                            <div style={{ height:6, borderRadius:3, background:"#e5e7eb", overflow:"hidden", minWidth:60 }}>
                              <div style={{ height:"100%", borderRadius:3, background:"#7c3aed", width:pct(min, datiOperaio.totLav) }}/>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:"#f9fafb", borderTop:"2px solid #e5e7eb" }}>
                        <td style={{ padding:"8px 16px", fontSize:12, fontWeight:700, color:"#374151" }}>TOTALE</td>
                        <td style={{ padding:"8px 16px", textAlign:"right", fontSize:13, fontWeight:800, color:"#059669" }}>{fmtOre(datiOperaio.totLav)}</td>
                        <td colSpan={2}/>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </>)}

            {!operaioSel && (
              <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
                <Users size={40} color="#d1d5db" style={{ marginBottom:12 }}/>
                <div style={{ fontSize:14 }}>Seleziona un dipendente per vedere il riepilogo ore</div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB COMMESSA ─────────────────────────────────────────────── */}
        {tab==="commessa" && (
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
              <div style={{ fontSize:13, color:"#6b7280" }}>
                Totale ore per commessa nel periodo <strong>{periodoLabel}</strong> — somma di tutti gli operai
              </div>
              {datiCommesse.length > 0 && (
                <button onClick={esportaCommesse}
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px",
                    border:"1px solid #e5e7eb", borderRadius:8, background:"#fff",
                    color:"#374151", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  <Download size={13}/> Esporta Excel
                </button>
              )}
            </div>

            {loading && <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Caricamento...</div>}

            {!loading && datiCommesse.length===0 && (
              <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
                <FolderOpen size={40} color="#d1d5db" style={{ marginBottom:12 }}/>
                <div style={{ fontSize:14 }}>Nessuna timbratura per commessa nel periodo selezionato</div>
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:12 }}>
              {datiCommesse.map(({ nome, minuti, operai }, i) => {
                const progetto = commesse.find(c => c.name===nome || c.codiceCommessa===nome);
                const orfana = !progetto;
                const archiviata = progetto?.archived;

                // Risolvi nomi operai
                const operaiArr = Object.entries(operai||{})
                  .sort((a,b) => b[1]-a[1])
                  .map(([uid, min]) => {
                    const dip = dipendenti.find(d => d.nexoraUid===uid);
                    const nome = dip ? `${dip.nome||""} ${dip.cognome||""}`.trim() : uid.slice(0,8)+"…";
                    return { nome, min };
                  });

                return (
                  <div key={i} style={{ background:orfana?"#f9fafb":"#fff", borderRadius:12,
                    border:"1px solid #e5e7eb", padding:"16px 20px",
                    display:"flex", flexDirection:"column", gap:10, opacity:orfana?0.75:1 }}>

                    {/* Header card */}
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2, flexWrap:"wrap" }}>
                          {progetto?.codiceCommessa && (
                            <span style={{ fontSize:10, fontWeight:700, color:"#7c3aed", fontFamily:"monospace" }}>
                              {progetto.codiceCommessa}
                            </span>
                          )}
                          {archiviata && <span style={{ fontSize:9, fontWeight:700, color:"#6b7280", background:"#f3f4f6", borderRadius:4, padding:"1px 6px" }}>ARCHIVIATA</span>}
                          {orfana && <span style={{ fontSize:9, fontWeight:700, color:"#9ca3af", background:"#f3f4f6", borderRadius:4, padding:"1px 6px" }} title="Commessa eliminata — ore storiche conservate">⚠️ ELIMINATA</span>}
                        </div>
                        <div style={{ fontSize:13, fontWeight:700, color:orfana?"#6b7280":"#111827", lineHeight:1.3 }}>{nome}</div>
                        {progetto?.clienteNome && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{progetto.clienteNome}</div>}
                        {orfana && <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, fontStyle:"italic" }}>Ore storiche conservate</div>}
                      </div>
                      <div style={{ background:"#d1fae5", borderRadius:8, padding:"6px 12px", textAlign:"center", flexShrink:0 }}>
                        <div style={{ fontSize:10, color:"#059669", fontWeight:700 }}>TOTALE ORE</div>
                        <div style={{ fontSize:20, fontWeight:800, color:"#059669" }}>{fmtOre(minuti)}</div>
                      </div>
                    </div>

                    {/* Barra proporzionale */}
                    <div style={{ height:4, borderRadius:2, background:"#e5e7eb", overflow:"hidden" }}>
                      <div style={{ height:"100%", borderRadius:2, background:"#7c3aed",
                        width:pct(minuti, datiCommesse[0]?.minuti||minuti) }}/>
                    </div>

                    {/* Dettaglio operai */}
                    {operaiArr.length > 0 && (
                      <div style={{ borderTop:"1px solid #f3f4f6", paddingTop:8 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", marginBottom:6 }}>OPERAI</div>
                        {operaiArr.map((op, j) => (
                          <div key={j} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                            <span style={{ fontSize:11, color:"#374151" }}>{op.nome}</span>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ width:50, height:4, borderRadius:2, background:"#e5e7eb", overflow:"hidden" }}>
                                <div style={{ height:"100%", borderRadius:2, background:"#a78bfa",
                                  width:pct(op.min, minuti) }}/>
                              </div>
                              <span style={{ fontSize:11, fontWeight:700, color:"#059669", minWidth:36, textAlign:"right" }}>
                                {fmtOre(op.min)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {tab==="cumulativo" && (
          <div style={{ padding:"24px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
              <div>
                <div style={{ fontSize:14, fontWeight:800, color:"#1e1b4b", marginBottom:4 }}>Riepilogo cumulativo</div>
                <div style={{ fontSize:12, color:"#6b7280" }}>Totale ore per tutti i dipendenti nel periodo selezionato.</div>
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button onClick={()=>esportaContabilita(periodoTipo==="mese"?mese:null)}
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
                    border:"1px solid #059669", borderRadius:8, background:"#d1fae5",
                    color:"#065f46", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  📊 Export contabilità {periodoTipo==="mese"?"mese":"anno"}
                </button>
                {periodoTipo==="anno" && (
                  <button onClick={()=>{
                    [1,2,3,4,5,6,7,8,9,10,11,12].forEach((m,i)=>{
                      setTimeout(()=>esportaContabilita(m), i*300);
                    });
                  }}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
                      border:"1px solid #7c3aed", borderRadius:8, background:"#ede9fe",
                      color:"#5b21b6", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    📋 Export per mese (12 file)
                  </button>
                )}
              </div>
            </div>
            {(()=>{
              const perDip = {};
              datiAnno.forEach(docData => {
                const uid = docData.uid;
                if (!perDip[uid]) perDip[uid] = { uid, minuti:0 };
                Object.values(docData.giorni||{}).forEach(g => {
                  // segmenti di lavoro dal calcolo unico (permessi a ore già tolti); pausa come prima
                  calcolaSegmenti(g.timbrature || []).filter(x => !x.speciale).forEach(x => {
                    const lordi = x.minuti + (x.minutiTolti || 0);
                    if (x.minuti > 0) perDip[uid].minuti += Math.max(0, x.minuti - (lordi >= 360 ? 60 : 0));
                  });
                });
              });
              const totaleMin = Object.values(perDip).reduce((s,d)=>s+d.minuti,0);
              const dipArr = Object.values(perDip).sort((a,b)=>b.minuti-a.minuti);
              if(dipArr.length===0) return (
                <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>
                  <div style={{ fontSize:14 }}>Nessun dato disponibile per il periodo selezionato</div>
                </div>
              );
              return (
                <div>
                  <div style={{ display:"flex", gap:16, marginBottom:20, flexWrap:"wrap" }}>
                    <div style={{ background:"#ede9fe", borderRadius:10, padding:"12px 20px" }}>
                      <div style={{ fontSize:11, color:"#7c3aed", fontWeight:600, marginBottom:2 }}>ORE TOTALI</div>
                      <div style={{ fontSize:24, fontWeight:800, color:"#5b21b6" }}>{(totaleMin/60).toFixed(1)}h</div>
                    </div>
                    <div style={{ background:"#f0fdf4", borderRadius:10, padding:"12px 20px" }}>
                      <div style={{ fontSize:11, color:"#059669", fontWeight:600, marginBottom:2 }}>DIPENDENTI ATTIVI</div>
                      <div style={{ fontSize:24, fontWeight:800, color:"#065f46" }}>{dipArr.length}</div>
                    </div>
                  </div>
                  <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12 }}>
                    <thead>
                      <tr style={{ background:"#1e1b4b" }}>
                        <th style={{ padding:"8px 12px", textAlign:"left", color:"#fff", fontSize:10, fontWeight:700 }}>Dipendente</th>
                        <th style={{ padding:"8px 12px", textAlign:"right", color:"#fff", fontSize:10, fontWeight:700, width:100 }}>Ore totali</th>
                        <th style={{ padding:"8px 12px", textAlign:"right", color:"#fff", fontSize:10, fontWeight:700, width:70 }}>%</th>
                        <th style={{ padding:"8px 12px", textAlign:"left", color:"#fff", fontSize:10, fontWeight:700 }}>Distribuzione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dipArr.map((d,i) => {
                        const dip = dipendenti.find(x=>x.nexoraUid===d.uid);
                        const nome = dip ? `${dip.nome||""} ${dip.cognome||""}`.trim() : d.uid?.slice(0,8)+"…";
                        const pct = totaleMin>0 ? Math.round(d.minuti/totaleMin*100) : 0;
                        return (
                          <tr key={d.uid} style={{ background:i%2===0?"#fff":"#fafafa", borderBottom:"1px solid #f3f4f6" }}>
                            <td style={{ padding:"8px 12px", fontWeight:600, color:"#111827" }}>{nome}</td>
                            <td style={{ padding:"8px 12px", textAlign:"right", fontWeight:700, color:"#059669" }}>{(d.minuti/60).toFixed(1)}h</td>
                            <td style={{ padding:"8px 12px", textAlign:"right", color:"#6b7280" }}>{pct}%</td>
                            <td style={{ padding:"8px 12px" }}>
                              <div style={{ background:"#e5e7eb", borderRadius:4, height:8, overflow:"hidden" }}>
                                <div style={{ background:"#7c3aed", height:"100%", width:`${pct}%`, borderRadius:4 }}/>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
